import moment from "moment";
import { useMemo, useState } from "react";
import {
  ListFieldsRecordType,
  emailsSender,
  paramsListName,
  siteUrl,
} from "src/api/config";
import { DrawerComponent, Modal } from "src/components/DialogComponent";
import { NotificationSmallIcon } from "src/components/ui-icons/card";
import TGButton from "src/components/ui/TGButton";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";
import SelectDocuments, { NotifyMyDocumentsType } from "./SelectDocuments";
import SelectReviewDate from "./SelectReviewDate";
import SelectComment from "./SelectComment";
import SelectEmployees, { NotifyMyEmployeesType } from "./SelectEmployees";

import { addItemRequest, getItemsRequest } from "src/modules/spItems/api";
import { getNotificationsInfo } from "src/modules/acadia/notifications/actions";
import { useFiltersContext } from "src/contexts/FilterContext";
import {
  notificationIdsListName,
  notificationsListName,
} from "src/api/config/notifications.config";
import { ITesting } from "../components/Tests/types";

import { selectCurrentUser } from "src/modules/users/selector";
import { emailAPI } from "src/utils/api.utils";
import { removeInvisibleChar } from "src/utils/string.utils";
import { emailQueueListName } from "src/api/config/email.config";
import { useStatusContext } from ".";
import { autoCloseModal } from "src/constants";

const { sendEmail, createEmailBodyByTemplate } = emailAPI();

type SubmitType = {
  documents: Map<number, NotifyMyDocumentsType>;
  reviewDate: Date;
  comment: string;
  employees: Map<number, NotifyMyEmployeesType>;
  test: ITesting;
};

export type UserType = { name: string; avatar: string };

export function NotificationSendOWDCalendarButton({
  changeActive,
  read,
  type,
  title = "Отправить уведомление",
  zIndex = 50,
  initialSubmit = {
    documents: new Map(),
    reviewDate: moment(new Date()).add(30, "day").toDate(),
    comment: "",
    employees: new Map(),
    test: {
      Questions: [],
      IsQuestionFormOpen: false,
      MinCorrectAnswersCount: 1,
      QuestionsId: [],
    },
  },
}: {
  changeActive(active: "get" | "send"): void;
  read?: Record<any, boolean>;
  title?: string;
  zIndex?: number;
  initialSubmit?: SubmitType;
  type?: string;
}) {
  const [submitState, setSubmitState] = useState<SubmitType>(
    _.cloneDeep(initialSubmit)
  );
  const [submitLoading, setSubmitLoading] = useState(false);
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => selectCurrentUser(state));
  const { resetFilters } = useFiltersContext();
  const [open, setOpen] = useState(false);
  const [openSubmitNotificationModal, setOpenSubmitNotificationModal] =
    useState(false);
  const { changeStatus } = useStatusContext();

  const toggleSubmitNotificationModal = (state: boolean) => {
    setOpenSubmitNotificationModal(state);
  };

  const clearSubmit = () => {
    setSubmitState(_.cloneDeep(initialSubmit));
    resetFilters();
  };

  const closeAllModals = () => {
    setOpen(false);
    setOpenSubmitNotificationModal(false);
    clearSubmit();
  };

  const isButtonDisabledMemoized = useMemo(() => {
    return (
      !!submitState.documents.size &&
      !!submitState.employees.size &&
      !!submitState.reviewDate
    );
  }, [submitState, submitState.documents]);

  async function handleOWDSubmit() {
    try {
      setSubmitLoading(true);

      const notificationID = (await addItemRequest({
        listName: notificationIdsListName,
        body: { Title: "OWD" },
        updateReducer: false,
      })) as number;

      const notificationsPromises = [];
      const emailPromises = [];
      const notifyMaps = new Map<
        string,
        Map<NotifyMyDocumentsType, number[]>
      >();

      for (const [keyEmp, emp] of submitState.employees) {
        const notifyMap = new Map<NotifyMyDocumentsType, number[]>();
        notifyMaps.set(emp.user.email.toLowerCase(), notifyMap);

        for (const [key, doc] of submitState.documents) {
          const submitObject = {
            Title: "OWD " + notificationID,
            NotificationID: notificationID,
            Employee: emp.user.email.toLowerCase(),
            EmployeeEmail: emp.user.email.toLowerCase(),
            Document: doc.id,
            DocumentCode: doc.code.code,
            ReviewDate: submitState.reviewDate,
            AuthorComment: submitState.comment,
            NotificationType: 6,
            AuthorEmail: currentUser.email.toLowerCase(),
          };

          const notificationPromise = addItemRequest({
            listName: notificationsListName,
            body: submitObject,
            updateReducer: false,
          }).then((id: unknown) => {
            const notificationsListID = id as number;

            if (notificationsListID > 0) {
              if (notifyMap.has(doc)) {
                const arrayBody = notifyMap.get(doc) || [];
                notifyMap.set(doc, [...arrayBody, notificationsListID]);
              } else {
                notifyMap.set(doc, [notificationsListID]);
              }
            }
          });

          notificationsPromises.push(notificationPromise);
        }
      }

      await Promise.all(notificationsPromises);

      if (emailsSender) {
        for (const [keyEmp, emp] of submitState.employees) {
          const notifyMap = notifyMaps.get(emp.user.email.toLowerCase());
          if (notifyMap) {
            const tempEmailBodyObject = {
              subject: `Мои Документы | Проведение OWD`,
              user: {
                name: emp.user.name + ` (${emp.user.personalNumber})`,
                email: emp.user.email,
              },
              document: {
                title: `Проведите OWD по следующему (-им) документу (-ам)`,
                lists: [...notifyMap.entries()].map(([doc, ids]) => ({
                  link: `${siteUrl}/dashboard/1/notification?notificationCard=${ids.join(
                    //* если что изменить на динамический
                    ","
                  )}`,
                  title: doc.code.name,
                })),
              },
              periodDate: {
                title: "Срок проведения OWD",
                date: submitState.reviewDate,
              },
              header: "Supply Portal -> Мои Документы | Проведение OWD",
            };

            const emailBody = createEmailBodyByTemplate(tempEmailBodyObject);

            const emailRequestBody: Partial<
              ListFieldsRecordType[typeof emailQueueListName]
            > = {
              Body: emailBody,
              DateToBeSentAt: new Date(),
              Title: "OWD",
              Subject: "Мои Документы | Проведение OWD",
              NotificationID: notificationID,
              Recepients: emp.user.email.toLowerCase(),
              RepeatAt: 0,
            };

            const emailPromise = sendEmail(emailRequestBody).then(async () => {
              const { items } = await getItemsRequest({
                listName: paramsListName,
                filters: { ParamsType: ["owd"] },
              });

              if (items[0] && items[0].Value) {
                const stringWithoutInvisibleChar = removeInvisibleChar(
                  items[0].Value
                );

                if (
                  submitState.reviewDate !== emailRequestBody.DateToBeSentAt
                ) {
                  const emailBody = createEmailBodyByTemplate({
                    ...tempEmailBodyObject,
                    periodDate: {
                      ...tempEmailBodyObject.periodDate,
                      expired: true,
                    },
                  });

                  emailRequestBody.Body = emailBody;
                  emailRequestBody.DateToBeSentAt = submitState.reviewDate;
                  emailRequestBody.RepeatAt = +stringWithoutInvisibleChar;

                  await sendEmail(emailRequestBody);
                }
              }
            });

            emailPromises.push(emailPromise);
          }
        }

        await Promise.all(emailPromises);
      }

      setOpenSubmitNotificationModal(true);
      changeActive("send");

      dispatch(
        getNotificationsInfo({
          activeNotifyList: "send",
          listName: notificationsListName,
        })
      );
    } catch (error) {
      console.log("error => handleOWDSubmit", error);
      throw new Error("Function not implemented.");
    } finally {
      setSubmitLoading(false);
      changeStatus(Date.now());
    }
  }

  return (
    <>
      {(type && type === "inCalendar" && (
        <FlexRow
          onClick={() => setOpen(!open)}
          className={twMerge(
            "w-full rounded text-[9E9E9E] hover:text-black hover:bg-[#F7F7F7] min-h-[23px] items-center px-1 cursor-pointer"
          )}
        >
          <FlexCol
            style={{ width: "71px" }}
            className="font-medium whitespace-pre-line"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="13"
              viewBox="0 0 12 13"
              fill="none"
            >
              <g opacity="0.3" clipPath="url(#clip0_1520_35482)">
                <path
                  d="M11.2496 6.0876H6.43086V1.2501C6.43086 1.0251 6.24336 0.818848 5.99961 0.818848C5.77461 0.818848 5.56836 1.00635 5.56836 1.2501V6.0876H0.749609C0.524609 6.0876 0.318359 6.2751 0.318359 6.51885C0.318359 6.74385 0.505859 6.9501 0.749609 6.9501H5.58711V11.7501C5.58711 11.9751 5.77461 12.1813 6.01836 12.1813C6.24336 12.1813 6.44961 11.9938 6.44961 11.7501V6.93135H11.2496C11.4746 6.93135 11.6809 6.74385 11.6809 6.5001C11.6809 6.2751 11.4746 6.0876 11.2496 6.0876Z"
                  fill="currentColor"
                />
              </g>
              <defs>
                <clipPath id="clip0_1520_35482">
                  <rect
                    width="12"
                    height="12"
                    fill="currentColor"
                    transform="translate(0 0.5)"
                  />
                </clipPath>
              </defs>
            </svg>
          </FlexCol>
        </FlexRow>
      )) || (
          <TGButton
            icon={<NotificationSmallIcon />}
            onClick={() => setOpen(!open)}
            color="bluedark"
          >
            {title}
          </TGButton>
        )}

      <DrawerComponent zIndex={zIndex} open={open} toggleDrawer={setOpen}>
        <header className="flex px-10 pt-10 pb-8 border-b shrink-0">
          <h1 className="text-black">Планирование OWD</h1>
        </header>
        <FlexCol className="divide-y grow">
          <SelectDocuments
            zIndex={zIndex}
            initial={submitState.documents}
            read={read?.documents}
            onResult={(items: Map<number, NotifyMyDocumentsType>) => {
              setSubmitState((prev) => ({ ...prev, documents: items }));
            }}
          />

          <SelectEmployees
            zIndex={zIndex}
            title="Кто проводит OWD"
            initial={submitState.employees}
            onResult={(items: Map<number, NotifyMyEmployeesType>) => {
              setSubmitState((prev) => ({ ...prev, employees: items }));
            }}
          />
          <SelectReviewDate
            zIndex={zIndex}
            title="Срок проведения OWD"
            modalTitle="Укажите срок проведения OWD"
            modalDescription="Срок проведения OWD"
            initial={submitState.reviewDate}
            onResult={(date: Date) => {
              setSubmitState((prev) => ({ ...prev, reviewDate: date }));
            }}
          />
          <SelectComment
            zIndex={zIndex}
            initial={submitState.comment}
            onResult={(text: string) => {
              setSubmitState((prev) => ({ ...prev, comment: text }));
            }}
          />
        </FlexCol>
        <footer className="flex justify-between p-10 border-t shrink-0">
          <TGButton
            disabled={submitLoading}
            onClick={() => {
              setOpen(false);
              clearSubmit();
            }}
          >
            Отмена
          </TGButton>
          <TGButton
            disabled={!isButtonDisabledMemoized || submitLoading}
            onClick={handleOWDSubmit}
            color="bluedark"
          >
            Отправить
          </TGButton>
        </footer>
      </DrawerComponent>
      <Modal
        zIndex={zIndex ? zIndex + 10 : 70}
        modalClassName="min-h-px"
        open={openSubmitNotificationModal}
        toggleDialog={toggleSubmitNotificationModal}
        autoClose={autoCloseModal}
        onAutoClose={() => {
          closeAllModals();
        }}
      >
        <header className="px-10 pb-4">
          <h2>Отправка уведомления</h2>
        </header>

        <div className="px-10 py-5">
          <p>Уведомления были успешно отправлены.</p>
        </div>

        <footer className="flex justify-center px-10 pt-10 mt-4 border-t">
          <TGButton color="bluedark" onClick={closeAllModals}>
            Закрыть
          </TGButton>
        </footer>
      </Modal>
    </>
  );
}
